import type { UseMutationOptions } from '@tanstack/react-query'
import { isHTTPError } from 'ky'
import {
  pollUntil,
  PollTimeoutError,
  type PollUntilOptions,
} from '~/helpers/poll-until'
import { useGatedMutation } from '~/lib/gated-mutation'
import { logNonFatalError } from '~/lib/log-errors'
import { openMutationErrorDialog } from '~/lib/mutation-error-dialog'
import { queryClient } from '~/lib/query-client'
import { m } from '~/paraglide/messages'
import { getActivity } from '~/services/api/gamepass-service'
import { purchaseProduct } from '~/services/api/store-service'
import { dialog } from '@forest/shared/react'
import { $userInfo } from '~/store/user-info'
import { activityQueryKey, getActivityId } from './use-activity-query'

const JUMP_POLL_OPTIONS: PollUntilOptions = {
  intervalMs: 1000,
  deadlineMs: 15_000,
}

export interface JumpVariables {
  productId: number
  requiredScore: number
}

/**
 * The mutation options behind useJumpMutation, exposed with injectable poll
 * timing so tests can drive the outcome routing without real timers.
 *
 * Purchases a jump point and waits for the score grant to land — the grant
 * is asynchronous server-side, so after a 2xx the activity is polled until
 * `progress.accumulated_score` reaches the jump point's score.
 *
 * The activity and balance queries are invalidated only on success. After a
 * polling timeout they intentionally stay stale: the purchase may still
 * land, and the user must never be led to buy twice.
 */
export function createJumpMutationOptions(
  pollOptions: PollUntilOptions = JUMP_POLL_OPTIONS,
): UseMutationOptions<void, Error, JumpVariables> {
  return {
    mutationFn: async ({ productId, requiredScore }) => {
      const activityId = getActivityId()
      const userId = $userInfo.get()!.user_id

      await purchaseProduct(productId)

      await pollUntil(
        async () => {
          try {
            return await getActivity(activityId, userId)
          } catch {
            // The purchase already landed, so a failed poll must never
            // surface as a retryable error — treat it as "not settled yet"
            // and let the deadline decide.
            return null
          }
        },
        (activity) =>
          activity !== null &&
          (activity.progress?.accumulated_score ?? 0) >= requiredScore,
        pollOptions,
      )
    },

    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: activityQueryKey(getActivityId()),
        }),
        queryClient.invalidateQueries({ queryKey: ['balance'] }),
      ]),

    onError: (error) => {
      logNonFatalError({
        name: 'failed_to_jump',
        code: 1,
        params: {
          message: String(error),
        },
      })

      if (error instanceof PollTimeoutError) {
        dialog.open({
          // TODO(#261): en placeholder — no Lokalise copy for the jump
          // timeout dialog yet.
          title: 'Progress Update Delayed',
          description:
            'Your purchase was received, but your progress is taking longer than expected to update. Please check back later — do not purchase again.',
          button: m.OK(),
        })
        return
      }

      // Insufficient Time Crystals — the balance changed since the
      // pre-check that opened the confirm dialog.
      if (isHTTPError(error) && error.response.status === 422) {
        dialog.open({
          title: m.dialog_purchase_insufficient_gems_title(),
          description: m.dialog_purchase_insufficient_gems_context(),
          // Deliberately `m.ok()` (not `m.OK()`): this dialog mirrors the
          // pre-check insufficient dialog in app.tsx, button key included.
          button: m.ok(),
        })
        return
      }

      // TODO(#261): generic cascade below pending final jump error copy.
      openMutationErrorDialog(error)
    },
  }
}

export function useJumpMutation() {
  return useGatedMutation(createJumpMutationOptions())
}
